import React from "react"
import { useMutation } from "urql"

import Breadcrumbs from "../../../components/shared/Breadcrumbs"
import { Button } from "../../../components/shared/Buttons"
import HealMeTooltip from "../../../components/shared/HealMeTooltip"
import { Banner } from "../../../components/shared/Layout"
import { ButtonLink } from "../../../components/shared/Links"
import Typography from "../../../components/shared/Typography"
import { ToastProvider, useToast } from "../../../contexts/ToastContext"
import GraphQLProvider from "../../../providers/GraphQLProvider"

const PAUSE_CAMPAIGN = `
  mutation PauseNewsletterCampaign($campaignId: ID!) {
    pauseNewsletterCampaign(campaignId: $campaignId) {
      result
      errors
    }
  }
`

const RESUME_CAMPAIGN = `
  mutation ResumeNewsletterCampaign($campaignId: ID!) {
    resumeNewsletterCampaign(campaignId: $campaignId) {
      result
      errors
    }
  }
`

const DUPLICATE_CAMPAIGN = `
  mutation DuplicateNewsletterCampaign($campaignId: ID!) {
    duplicateNewsletterCampaign(campaignId: $campaignId) {
      result
      errors
      campaign { id }
    }
  }
`

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-light p-4">
      <Typography variant="body" className="text-gray-dark">
        {label}
      </Typography>
      <Typography variant="h4">{value}</Typography>
    </div>
  )
}

function formatDateTime(value) {
  if (!value) return "—"
  return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
}

function EngagementCount({ count, events, tooltipId, title }) {
  if (!count) return "0"

  return (
    <>
      <span className="cursor-help border-b border-dotted border-gray-dark" data-tooltip-id={tooltipId}>
        {count}
      </span>
      <HealMeTooltip
        id={tooltipId}
        place="left"
        render={() => (
          <div className="space-y-1 text-left font-normal">
            <div className="font-semibold">{title}</div>
            {(events || []).map((event, index) => (
              <div key={`${event.occurredAt}-${index}`} className="text-xs">
                <div>{formatDateTime(event.occurredAt)}</div>
                {event.linkUrl && (
                  <div className="truncate text-gray-light" title={event.linkUrl}>
                    {event.linkUrl}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      />
    </>
  )
}

function NewsletterCampaignDetail({ campaign }) {
  const { showToast } = useToast()
  const [, pauseCampaign] = useMutation(PAUSE_CAMPAIGN)
  const [, resumeCampaign] = useMutation(RESUME_CAMPAIGN)
  const [, duplicateCampaign] = useMutation(DUPLICATE_CAMPAIGN)

  const handlePause = async () => {
    const result = await pauseCampaign({ campaignId: campaign.id })
    if (result.data?.pauseNewsletterCampaign?.result === "success") {
      showToast("Campaign paused")
      window.location.reload()
    }
  }

  const handleResume = async () => {
    const result = await resumeCampaign({ campaignId: campaign.id })
    if (result.data?.resumeNewsletterCampaign?.result === "success") {
      showToast("Campaign resumed")
      window.location.reload()
    } else {
      showToast({
        type: "error",
        content: result.data?.resumeNewsletterCampaign?.errors?.[0] || "Resume failed"
      })
    }
  }

  const handleDuplicate = async () => {
    const result = await duplicateCampaign({ campaignId: campaign.id })
    if (result.data?.duplicateNewsletterCampaign?.result === "success") {
      const newId = result.data.duplicateNewsletterCampaign.campaign.id
      window.location.href = `/portal/settings/newsletter/campaigns/${newId}/edit`
    }
  }

  return (
    <div>
      <Breadcrumbs
        links={[
          { href: "/portal/settings", text: "Settings" },
          { href: "/portal/settings/newsletter/campaigns", text: "Campaigns" }
        ]}
        activePage={campaign.subject || "Campaign"}
      />

      <div className="mb-6 flex items-start justify-between gap-4 sm:flex-col">
        <div>
          <Typography as="h1" variant="h3">
            {campaign.subject || "(No subject)"}
          </Typography>
          <Typography variant="body" className="text-gray-dark">
            Status: {campaign.status}
          </Typography>
        </div>
        <div className="flex flex-wrap gap-2">
          {campaign.status === "paused" && (
            <Button type="primary" onClick={handleResume}>
              Resume
            </Button>
          )}
          {campaign.status === "sending" && campaign.recipientCount === 0 && (
            <Button type="primary" onClick={handleResume}>
              Retry send
            </Button>
          )}
          {["sending", "scheduled"].includes(campaign.status) && (
            <Button type="secondary" onClick={handlePause}>
              Pause
            </Button>
          )}
          <Button type="tertiary" onClick={handleDuplicate}>
            Duplicate
          </Button>
        </div>
      </div>

      {campaign.pauseReason && (
        <Banner type="warning" className="mb-6">
          {campaign.pauseReason}
        </Banner>
      )}

      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-3 md:grid-cols-1">
        <StatCard label="Sent" value={campaign.sentCount} />
        <StatCard label="Failed" value={campaign.failedCount} />
        <StatCard label="Unique opens" value={campaign.openCount} />
        <StatCard label="Unique clicks" value={campaign.clickCount} />
        <StatCard label="Unsubscribes" value={campaign.unsubscribeCount} />
        <StatCard label="Recipients" value={campaign.recipientCount} />
      </div>

      <Banner type="info" className="mb-6">
        Open rates may be inflated by Apple Mail Privacy Protection and other privacy features.
      </Banner>

      <Typography variant="h5" className="mb-4">
        Recipients
      </Typography>
      <div className="overflow-hidden rounded-xl border border-gray-light">
        <table className="min-w-full divide-y divide-gray-light">
          <thead className="bg-gray-ultralight">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Client</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Sent</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Opens</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Clicks</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Error</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-light bg-white">
            {(campaign.recipients || []).map((recipient) => (
              <tr key={recipient.id}>
                <td className="px-4 py-3">{recipient.clientName || "—"}</td>
                <td className="px-4 py-3">{recipient.toEmail}</td>
                <td className="px-4 py-3">{recipient.status}</td>
                <td className="px-4 py-3 text-sm">{formatDateTime(recipient.sentAt)}</td>
                <td className="px-4 py-3">
                  <EngagementCount
                    count={recipient.openCount}
                    events={recipient.openEvents}
                    tooltipId={`recipient-open-${recipient.id}`}
                    title="Opens"
                  />
                </td>
                <td className="px-4 py-3">
                  <EngagementCount
                    count={recipient.clickCount}
                    events={recipient.clickEvents}
                    tooltipId={`recipient-click-${recipient.id}`}
                    title="Clicks"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-red">{recipient.errorMessage || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <ButtonLink href="/portal/settings/newsletter/campaigns" variant="secondary">
          Back to campaigns
        </ButtonLink>
      </div>
    </div>
  )
}

export default function NewsletterCampaignDetailPage(props) {
  return (
    <GraphQLProvider>
      <ToastProvider>
        <NewsletterCampaignDetail {...props} />
      </ToastProvider>
    </GraphQLProvider>
  )
}
